import angular from "angular"
import { StateParams, StateService } from "angular-ui-router"

import CkRedirect from "../../app/model/domain/ck-redirect"
import type ClassCodeService from "../../app/services/class-code/class-code.service"
import type BreadcrumbService from "../../app/services/breadcrumb-service/breadcrumb.service"
import type AuthService from "../../app/services/auth/auth.service"
import type ToolbarService from "../../app/services/toolbar/toolbar.service"
import type CacheService from "../../app/services/cache/cache.service"
import type RosterService from "../../app/services/roster/roster.service"
import type StorageService from "../../app/services/storage/storage.service"

interface IClassCodeData {
  allowNewMembers: boolean
  pro: boolean
  hideStudentWork: boolean
  names: string[]
}

export class LoginSources {
  static get LOGIN(): string {
    return "Login"
  }
  static get SIGN_UP(): string {
    return "Sign up"
  }
}

type LoginPageControllerScope = angular.IScope & {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  anonStudentLoginForm: any
}

export class LoginPageController {
  public allowNewMembers = false
  public classCode: string
  public classCodePattern = "^[a-zA-Z\\d\\s]+$"
  public errorMessage = ""
  public focusClassCode = true
  public focusName = false
  public hideStudentWork?: boolean
  public isLoading = false
  public name: string
  public showIsteLink = false

  private checkingClassCode = ""
  private classCodeIsValid = false
  private classCodeRegex = new RegExp(this.classCodePattern)
  private isCleverClassCode = false
  private isPro?: boolean
  private nameIsPresent = false
  private nameIsValid = false
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private nameTimeout: any = null
  private studentNames = new Set<string>()

  constructor(
    private $q: angular.IQService,
    private $log: angular.ILogService,
    private $timeout: angular.ITimeoutService,
    private $state: StateService,
    private $stateParams: StateParams,
    private $scope: LoginPageControllerScope,
    public $mdDialog: angular.material.IDialogService,
    private $document: angular.IDocumentService,
    private ClassCodeService: ClassCodeService,
    private BreadcrumbService: BreadcrumbService,
    private AuthService: AuthService,
    private ToolbarService: ToolbarService,
    private CacheService: CacheService,
    private RosterService: RosterService,
    private StorageService: StorageService,
  ) {
    "ngInject"

    this.classCode = $stateParams.classCode
    this.name = $stateParams.name

    $timeout(() => this.classCodeUpdated())
  }

  public async checkClassCodeValidity(code: string): Promise<{
    isValid: boolean
    hideStudentWork?: boolean
    errorMessage?: string
  }> {
    const cleanedCode = code.toUpperCase().replace(/[\\s]/g, "")
    if (cleanedCode.length !== 6) {
      return { isValid: false }
    }

    try {
      const classCodeData: IClassCodeData =
        await this.ClassCodeService.getUsernames(cleanedCode)

      return {
        isValid: true,
        hideStudentWork: classCodeData.hideStudentWork,
      }
    } catch (err) {
      let errorMessage = "Class code not found."
      if ((err as Error).message === "clever-import created") {
        errorMessage = "This class uses Clever. Please log in with Clever."
      }
      return { isValid: false, errorMessage }
    }
  }

  get anonStudentLoginButtonMessage(): string {
    return this.canResume ? "Continue" : "Go"
  }

  classCodeUpdated(): void {
    if (this.$scope.anonStudentLoginForm) {
      this.$scope.anonStudentLoginForm.classCode.$touched = true
      this.$scope.anonStudentLoginForm.name.$touched = true
      this.$scope.anonStudentLoginForm.classCode.$setValidity("bad-ccode", true)
      this.$scope.anonStudentLoginForm.classCode.$setValidity("pattern", true)
    }

    this.studentNames = new Set()
    this.nameUpdated()
    this.classCodeIsValid = false

    const classCodeMaxLength = this.classCode.indexOf(" ") > -1 ? 7 : 6
    this.classCode = this.classCode.toUpperCase().slice(0, classCodeMaxLength)

    if (!this.classCode) {
      return
    } else if (!this.classCodeRegex.test(this.classCode)) {
      if (this.$scope.anonStudentLoginForm) {
        this.$scope.anonStudentLoginForm.classCode.$setValidity(
          "pattern",
          false,
        )
      }
      return
    }

    const cleanedClassCode = this.removeSpaces(this.classCode)
    if (cleanedClassCode.length === 6) {
      if (this.checkingClassCode !== cleanedClassCode) {
        this.isLoading = true
        this.checkingClassCode = cleanedClassCode
        this.loadClassCodeData(cleanedClassCode)
      }
    } else {
      this.checkingClassCode = ""
    }
  }

  private removeSpaces(code: string): string {
    return code.replace(/[\s]/g, "")
  }

  // Verifies that a classCode exists, and then loads the students for that classCode
  public loadClassCodeData(tempClassCode: string): void {
    this.ClassCodeService.getUsernames(tempClassCode)
      .then((classCodeData) => {
        this.isLoading = false
        if (this.removeSpaces(this.classCode) !== tempClassCode) {
          throw new Error("class Code was modified")
        }
        this.$scope.anonStudentLoginForm.classCode.$setValidity(
          "bad-ccode",
          true,
        )
        this.allowNewMembers = classCodeData.allowNewMembers
        this.classCodeIsValid = true
        this.focusName = true
        this.isPro = classCodeData.pro
        this.hideStudentWork = classCodeData.hideStudentWork
        this.studentNames = new Set(
          classCodeData.names.map((name: string) => name.toLowerCase()),
        )
        this.nameUpdated()
      })
      .catch((err: Error) => {
        if (this.$scope.anonStudentLoginForm) {
          if (err.message === "clever-import created") {
            this.isCleverClassCode = true
            this.$scope.anonStudentLoginForm.classCode.$setValidity(
              "clever-locked",
              false,
            )
          } else {
            this.$scope.anonStudentLoginForm.classCode.$setValidity(
              "bad-ccode",
              false,
            )
          }
        }
        this.$log.debug("_loadClassCodeData error", err)
        this.isLoading = false
      })
  }

  nameUpdated(): void {
    if (this.$scope.anonStudentLoginForm) {
      this.$scope.anonStudentLoginForm.classCode.$touched = true
      this.$scope.anonStudentLoginForm.name.$touched = true
    }

    this.$timeout.cancel(this.nameTimeout)
    this.nameIsValid = false
    this.nameIsPresent = false

    if (this.name) {
      this.nameIsValid = this.studentNames.has(this.name.toLowerCase())
      this.nameIsPresent = this.nameIsValid || this.allowNewMembers
      if (!this.nameIsPresent) {
        this.nameTimeout = this.$timeout(() => {
          this.$scope.anonStudentLoginForm.name.$setValidity(
            "no-student",
            false,
          )
        }, 500)
      } else {
        this.$scope.anonStudentLoginForm.name.$setValidity("no-student", true)
      }
    }
  }

  login(): void {
    if (!this.canStartNew && !this.canResume) {
      return
    }
    this.ToolbarService.reset()
    this.CacheService.reset()
    this.$state.go("root.anon-student-assignment-work", {
      classCode: this.removeSpaces(this.classCode),
      name: this.name,
      source: this.canStartNew ? LoginSources.SIGN_UP : LoginSources.LOGIN,
      question: 1,
    })
  }

  get needClassCode(): boolean {
    return (
      !this.classCode ||
      this.removeSpaces(this.classCode).length !== 6 ||
      !this.classCodeIsValid
    )
  }

  get needName(): boolean {
    return (
      !this.needClassCode &&
      (!this.name || (!this.nameIsValid && !this.allowNewMembers))
    )
  }

  get canStart(): boolean {
    return !this.needClassCode && !this.needName
  }

  get canResume(): boolean {
    return this.canStart && this.nameIsValid
  }

  get canStartNew(): boolean {
    return !this.canResume && !this.needClassCode && !this.needName
  }

  get classCodeMinLength(): number {
    if (!this.name) {
      return 0
    }
    return 6
  }

  goToAccountLogin(): void {
    if (this.classCodeValid || this.isCleverClassCode) {
      this.StorageService.ckRedirect = new CkRedirect("root.account.home", {
        classCode: this.removeSpaces(this.classCode),
      })
    }
    this.BreadcrumbService.go("root.account-login", {
      classCode: this.removeSpaces(this.classCode),
    })
  }

  goToCreateAccountChoice(): void {
    this.BreadcrumbService.go("root.signup-choice")
  }

  get whatIsAClassCodeModalId(): string {
    return "what-is-a-class-code"
  }

  showClassCodeModal(): void {
    this.$mdDialog.show({
      contentElement: `#${this.whatIsAClassCodeModalId}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      parent: angular.element((this.$document[0] as any).body),
      onRemoving: () => {
        this.focusClassCode = true
      },
    })
  }

  get classCodeValid(): boolean {
    return this.classCodeIsValid
  }

  get showPro(): boolean {
    return this.classCodeIsValid && this.isPro === true && !this.hideStudentWork
  }

  get title(): string {
    if (this.showPro) {
      return "Classkick Pro"
    }
    return "Start Working"
  }

  goToCreateStudentAccount(): void {
    const classCode = this.removeSpaces(this.classCode)
    this.StorageService.ckRedirect = new CkRedirect("root.account.home", {
      classCode,
    })
    this.BreadcrumbService.go("root.signup-student", { classCode })
  }

  goToIsteLoginPage(): void {
    window.location.href = "https://classkick.com/iste"
  }

  goBack(): void {
    this.classCode = ""
    this.classCodeUpdated()
  }
}
